/**
 * Requirement-set gating.
 *
 * AGENTS.md: every Office.js API is checked with `isSetSupported` **at call
 * time**, not at module load. Availability varies by platform, Office build and
 * licence type, and a value cached during startup is wrong for the user whose
 * Office updated underneath them mid-session.
 */
export const REQUIREMENTS = {
  /** Baseline declared in the manifest. */
  powerPointBase: { name: 'PowerPointApi', version: '1.3' },
  /** Shape insertion and slide/shape collections. */
  powerPointShapes: { name: 'PowerPointApi', version: '1.4' },
  /** Tag support on shapes and slides. */
  powerPointTags: { name: 'PowerPointApi', version: '1.3' },
  /** `getFileAsync(Office.FileType.Compressed)`. */
  file: { name: 'File', version: '1.1' },
  /** `Office.ui.displayDialogAsync`. */
  dialog: { name: 'DialogApi', version: '1.1' },
  /** `Office.context.document.settings`. */
  settings: { name: 'Settings', version: '1.1' },
} as const;

export type RequirementKey = keyof typeof REQUIREMENTS;

export interface Requirement {
  readonly name: string;
  readonly version: string;
}

export function requirementLabel(req: Requirement): string {
  return `${req.name} ${req.version}`;
}

/** True when Office.js has loaded and initialised. Never throws. */
export function isOfficeAvailable(): boolean {
  try {
    return typeof Office !== 'undefined' && Office.context !== undefined;
  } catch {
    return false;
  }
}

export function isSupported(req: Requirement): boolean {
  if (!isOfficeAvailable()) return false;
  try {
    return Office.context.requirements.isSetSupported(req.name, req.version) === true;
  } catch {
    // Some hosts throw rather than returning false for an unknown set name.
    return false;
  }
}

export interface HostInfo {
  readonly platform: string;
  readonly hostName: string;
  readonly officeVersion: string;
  readonly displayLanguage: string;
  readonly contentLanguage: string;
  readonly supported: Readonly<Record<RequirementKey, boolean>>;
}

/**
 * A snapshot for the diagnostics panel and for compatibility notes.
 * Safe to call outside Office: everything falls back to `unknown`.
 */
export function hostInfo(): HostInfo {
  const supported = Object.fromEntries(
    (Object.keys(REQUIREMENTS) as RequirementKey[]).map((key) => [key, isSupported(REQUIREMENTS[key])]),
  ) as Record<RequirementKey, boolean>;

  if (!isOfficeAvailable()) {
    return {
      platform: 'unknown',
      hostName: 'unknown',
      officeVersion: 'unknown',
      displayLanguage: 'unknown',
      contentLanguage: 'unknown',
      supported,
    };
  }

  const context = Office.context;
  return {
    platform: String(context.platform ?? 'unknown'),
    hostName: String(context.host ?? 'unknown'),
    officeVersion: context.diagnostics?.version ?? 'unknown',
    displayLanguage: context.displayLanguage ?? 'unknown',
    contentLanguage: context.contentLanguage ?? 'unknown',
    supported,
  };
}

/**
 * The UI language for the add-in.
 *
 * Requirement: none — `displayLanguage` is on the base context. Degradation:
 * falls back to id-ID, the product default, rather than to the browser locale,
 * which in a lab machine is often not the user's language at all.
 */
export function displayLanguage(fallback = 'id-ID'): string {
  if (!isOfficeAvailable()) return fallback;
  return Office.context.displayLanguage || fallback;
}
